// IND-034: «Coste por 100 m» de la vista Desperdicio. Carga aparte porque usa el
// cálculo de planta. Reparte el desperdicio según el material que consume cada
// producto; no mide qué producto genera más.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { getWasteCost } from '../services/industrialCostService';
import type { WasteCostView } from '../services/industrialCostService';
import { esNumberFormat } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import { MONTH_NAMES, MONTH_SHORT, per100, wasteCostProducts, wasteCostTotals, wasteOutOfShare } from '../utils/wasteControl';
import type { WasteCostProductRow, WasteCostTotals } from '../utils/wasteControl';
import SortableHeaderCell from '../components/SortableHeaderCell';
import { DetailError, DetailLoading } from './IndustrialCostMaterialDetailSections';
import IndustrialCostWasteOutOfShareDialog from './IndustrialCostWasteOutOfShareDialog';

const MUTED = '#94a3b8';
const PANEL = {
  bgcolor: '#151f30',
  border: '1px solid rgba(148,163,184,0.15)',
  borderRadius: 3,
  boxShadow: 'none',
};
const whole = esNumberFormat({ maximumFractionDigits: 0 });
const decimal = esNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 });
const weightFormat = esNumberFormat({ minimumFractionDigits: 3, maximumFractionDigits: 3 });

function fmt(value: number | null, format: Intl.NumberFormat, unit = ''): string {
  return value === null ? '—' : `${format.format(value)}${unit}`;
}

type Metric = 'metres' | 'kg' | 'eur';
const METRICS: Record<Metric, { value: (totals: WasteCostTotals) => number | null; text: (value: number | null) => string }> = {
  metres: { value: (totals) => (totals.metres > 0 ? totals.metres : null), text: (value) => fmt(value, whole) },
  kg: { value: (totals) => per100(totals.waste_kg, totals.metres), text: (value) => fmt(value, decimal) },
  eur: { value: (totals) => per100(totals.eur_total, totals.eur_metres), text: (value) => fmt(value, decimal) },
};

export default function IndustrialCostWasteCostSection({ year }: { year: number }) {
  const query = useQuery({
    // Con el prefijo de la proyección: las cargas que la invalidan, también.
    queryKey: ['industrial-cost-material-stock-projection', 'waste', 'cost', year],
    queryFn: () => getWasteCost(year),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const view = query.data;
  const [mode, setMode] = useState<'month' | 'year'>('month');
  const [selected, setSelected] = useState<number | null>(null);
  const [outOpen, setOutOpen] = useState(false);
  const lastMonth = view?.months[view.months.length - 1]?.month ?? null;
  const month = view?.months.some((item) => item.month === selected) ? selected : lastMonth;
  const scope = mode === 'month' ? month : null;
  const out = useMemo(() => (view ? wasteOutOfShare(view, scope) : null), [view, scope]);
  const hasFallback = (view?.destination_fallback_months ?? []).some((item) => scope === null || item === scope);
  const hasOut = Boolean(out && (out.products.length > 0 || out.materials.length > 0 || hasFallback));
  return (
    <Paper sx={{ ...PANEL, p: 1.5 }}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: 14 }}>Coste por 100 m</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>
            Reparto según el material que consume cada producto; no mide qué producto genera más desperdicio
          </Typography>
        </Box>
        {out && hasOut ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setOutOpen(true)}
            sx={{ textTransform: 'none', fontSize: 12, color: '#fbbf24', borderColor: 'rgba(251,191,36,0.45)' }}
          >
            {out.products.length > 0
              ? `Fuera del reparto · ${out.products.length} productos · ${fmt(out.metres, whole)} m (${fmt(out.pct, decimal, ' %')} de lo fabricado)`
              : 'Avisos del reparto'}
          </Button>
        ) : null}
      </Stack>
      {query.isError ? (
        <DetailError error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : !view ? (
        <DetailLoading height={220} />
      ) : view.months.length === 0 ? (
        <Typography sx={{ color: MUTED, fontSize: 13 }}>{`Sin meses con consumo en ${view.year}.`}</Typography>
      ) : (
        <Stack spacing={1.5}>
          <GroupAverages view={view} />
          <ProductShare
            view={view}
            mode={mode}
            onMode={setMode}
            month={month ?? view.months[0].month}
            onMonth={setSelected}
          />
        </Stack>
      )}
      {view && out ? (
        <IndustrialCostWasteOutOfShareDialog
          open={outOpen}
          onClose={() => setOutOpen(false)}
          out={out}
          fallbackMonths={view.destination_fallback_months}
          year={view.year}
          month={scope}
        />
      ) : null}
    </Paper>
  );
}

function GroupAverages({ view }: { view: WasteCostView }) {
  const [metric, setMetric] = useState<Metric>('eur');
  const sort = useTableSort<string>(null, 'desc');
  const months = view.months.map((item) => item.month);
  const definition = METRICS[metric];
  const cell = (group: string, month: number | null) => definition.value(wasteCostTotals(view, group, month));
  const value = (group: { group: string; label: string }): SortValue =>
    sort.key === 'group' ? group.label : cell(group.group, sort.key === 'year' ? null : Number(sort.key));
  const rows = sort.key ? sortRows(view.groups, value, sort.direction) : view.groups;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Media del grupo</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(_, next: Metric | null) => next && setMetric(next)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1, py: 0.25 } }}
        >
          <ToggleButton value="eur">€ por 100 m</ToggleButton>
          <ToggleButton value="kg">kg por 100 m</ToggleButton>
          <ToggleButton value="metres">Metros</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.75, fontSize: 12 } }}>
          <TableHead>
            <TableRow>
              <SortableHeaderCell label="Grupo" columnKey="group" sort={sort} firstDirection="asc" />
              {months.map((month) => (
                <SortableHeaderCell key={month} label={MONTH_SHORT[month - 1]} columnKey={String(month)} sort={sort} align="right" />
              ))}
              <SortableHeaderCell label="Año" columnKey="year" sort={sort} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((group) => (
              <TableRow key={group.group}>
                <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{group.label}</TableCell>
                {months.map((month) => {
                  const partial = metric === 'eur' && wasteCostTotals(view, group.group, month).price_partial;
                  return (
                    <TableCell
                      key={month}
                      align="right"
                      title={partial ? 'Precio parcial' : undefined}
                      sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: partial ? '#fbbf24' : undefined }}
                    >
                      {definition.text(cell(group.group, month))}
                    </TableCell>
                  );
                })}
                <TableCell
                  align="right"
                  title={metric === 'eur' && wasteCostTotals(view, group.group, null).price_partial ? 'Precio parcial' : undefined}
                  sx={{
                    fontWeight: 900,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    color: metric === 'eur' && wasteCostTotals(view, group.group, null).price_partial ? '#fbbf24' : undefined,
                  }}
                >
                  {definition.text(cell(group.group, null))}
                  {metric === 'eur' ? (
                    <Typography component="span" sx={{ display: 'block', color: MUTED, fontSize: 10, fontWeight: 600 }}>
                      {`meses con €: ${wasteCostTotals(view, group.group, null).eur_months}`}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

type ProductSortKey = 'code' | 'name' | 'metres' | 'weight' | 'kg' | 'eur' | 'kg100' | 'eur100' | 'share';
const PRODUCT_COLUMNS: Array<{
  key: ProductSortKey;
  label: string;
  value: (row: WasteCostProductRow) => SortValue;
  text?: (row: WasteCostProductRow) => string;
}> = [
  { key: 'code', label: 'Código', value: (row) => row.code },
  { key: 'name', label: 'Nombre', value: (row) => row.name },
  { key: 'metres', label: 'Metros', value: (row) => row.metres, text: (row) => fmt(row.metres, whole) },
  { key: 'weight', label: 'Peso kg/m', value: (row) => row.weight_per_metre, text: (row) => fmt(row.weight_per_metre, weightFormat) },
  { key: 'kg', label: 'Kg', value: (row) => row.waste_kg, text: (row) => fmt(row.waste_kg, whole) },
  { key: 'eur', label: '€', value: (row) => row.waste_eur, text: (row) => fmt(row.waste_eur, whole, ' €') },
  { key: 'kg100', label: 'Kg por 100 m', value: (row) => row.kg_per_100m, text: (row) => fmt(row.kg_per_100m, decimal) },
  { key: 'eur100', label: '€ por 100 m', value: (row) => row.eur_per_100m, text: (row) => fmt(row.eur_per_100m, decimal, ' €') },
  { key: 'share', label: '% del total', value: (row) => row.share_pct, text: (row) => fmt(row.share_pct, decimal, ' %') },
];

function ProductShare({
  view,
  mode,
  onMode,
  month,
  onMonth,
}: {
  view: WasteCostView;
  mode: 'month' | 'year';
  onMode: (mode: 'month' | 'year') => void;
  month: number;
  onMonth: (month: number) => void;
}) {
  const [group, setGroup] = useState(view.groups[0].group);
  const sort = useTableSort<ProductSortKey>('eur', 'desc');
  const scope = mode === 'month' ? month : null;
  const rows = useMemo(() => wasteCostProducts(view, group, scope), [view, group, scope]);
  const column = PRODUCT_COLUMNS.find((entry) => entry.key === sort.key) ?? PRODUCT_COLUMNS[5];
  const sorted = sortRows(rows, column.value, sort.direction);
  const totals = wasteCostTotals(view, group, scope);
  const partial = totals.price_partial;
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Reparto entre productos</Typography>
        {partial ? <Typography sx={{ color: '#fbbf24', fontSize: 11 }}>precio parcial</Typography> : null}
        {mode === 'year' ? <Typography sx={{ color: MUTED, fontSize: 11 }}>{`meses con €: ${totals.eur_months}`}</Typography> : null}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next: 'month' | 'year' | null) => next && onMode(next)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1, py: 0.25 } }}
        >
          <ToggleButton value="month">Mes</ToggleButton>
          <ToggleButton value="year">Año</ToggleButton>
        </ToggleButtonGroup>
        {mode === 'month' ? (
          <TextField select size="small" value={month} onChange={(event) => onMonth(Number(event.target.value))} sx={{ minWidth: 140 }}>
            {view.months.map((item) => (
              <MenuItem key={item.month} value={item.month}>{MONTH_NAMES[item.month - 1]}</MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField select size="small" value={group} onChange={(event) => setGroup(event.target.value)} sx={{ minWidth: 220 }}>
          {view.groups.map((item) => (
            <MenuItem key={item.group} value={item.group}>{item.label}</MenuItem>
          ))}
        </TextField>
      </Stack>
      {sorted.length === 0 ? (
        <Typography sx={{ color: MUTED, fontSize: 13 }}>Sin productos con peso en este grupo.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 'max(360px, calc(100vh - 120px))' }}>
          <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 0.75, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                {PRODUCT_COLUMNS.map((entry) => (
                  <SortableHeaderCell
                    key={entry.key}
                    label={entry.label}
                    columnKey={entry.key}
                    sort={sort}
                    align={entry.text ? 'right' : 'left'}
                    firstDirection={entry.text ? 'desc' : 'asc'}
                    sx={{ bgcolor: '#151f30' }}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={row.product_id}>
                  {PRODUCT_COLUMNS.map((entry) => (
                    <TableCell
                      key={entry.key}
                      align={entry.text ? 'right' : 'left'}
                      sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: entry.key === 'name' ? undefined : 'nowrap', fontWeight: entry.key === 'code' ? 800 : undefined }}
                    >
                      {entry.text ? entry.text(row) : (entry.key === 'code' ? row.code : row.name) ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
